import { JSX, PureComponent } from "react";
import moment from "moment";

import { QESButton, QESTextInput, QESTextArea, QESSelect, QESCheckbox } from "@app/shared/qes-standards";
import { FilterCriteria, FilterCriteriaModel, LoadingSpinner } from "@app/shared";

import ProviderSetService from "@app/provider-sets/providerSetService";
import { ProviderSetsRequestType } from "@app/provider-sets/enumerations";
import { ProviderSet, ProviderSetVersionModel } from "@app/provider-sets/providerSet";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

type EditPanelProps = {
  enableSourceEditing?: boolean;
  loading: boolean;
  filterCriteria?: FilterCriteriaModel[];
  filterCriteriaColumnNames?: string[];
  providerSet: ProviderSet;
  sourceProviderSet?: ProviderSet;
  sourceProviderSetVersion?: ProviderSetVersionModel;
  showIsPrivate: boolean;
  handleSave: (name: string, description: string, isPrivate: boolean, sourceProviderSetId: number, filterCriteria: FilterCriteriaModel[]) => void;
} & FlyoutContextOutgoingProps & FeatureFlagContextProps;

interface EditPanelState {
  description: string;
  enableSourceEditing: boolean;
  filterCriteria: FilterCriteriaModel[];
  filterCriteriaColumnNames: string[];
  filterCriteriaHasErrors: boolean;
  isPrivate: boolean;
  loading: boolean;
  name: string;
  sourceProviderSetId?: number;
  sourceProviderSetOptions: JSX.Element[];
}

class WrappedEditPanelContent extends PureComponent<EditPanelProps, EditPanelState> {
  private readonly providerSetService = new ProviderSetService();

  constructor(props: EditPanelProps) {
    super(props);
    this.state = {
      description: this.props.providerSet.resource.description,
      enableSourceEditing: this.props.enableSourceEditing,
      filterCriteria: this.props.filterCriteria,
      filterCriteriaColumnNames: this.props.filterCriteriaColumnNames,
      filterCriteriaHasErrors: false,
      isPrivate: this.props.providerSet.resource.isPrivate,
      loading: false,
      name: this.props.providerSet.resource.name,
      sourceProviderSetOptions: []
    };
  }

  public async componentDidMount() {
    this.setState({ loading: true });
    await this.initSourceProviderOptions();

    this.setState({
      loading: false,
    });
  }

  private async initSourceProviderOptions() {
    if (!this.props.providerSet.sourceProviderSetId || !this.props.enableSourceEditing) {
      this.setState({ enableSourceEditing: false });
      return;
    }

    const sourceProviderSets = await this.providerSetService.getProviderSetsByRequestType(ProviderSetsRequestType.SourceProviders, 0, 0);

    const activeProviderSets = sourceProviderSets.providerSetSimpleList.filter((x) => !!x.lastActivationDate);

    if (activeProviderSets.length === 0) {
      this.setState({ enableSourceEditing: false });
      return;
    }

    let defaultSourceProviderSetId = activeProviderSets[0].providerSetId;
    if (!!this.props.providerSet.sourceProviderSetId && activeProviderSets.some(x => x.providerSetId === this.props.providerSet.sourceProviderSetId)) {
      defaultSourceProviderSetId = this.props.sourceProviderSet.resource.resourceId;
    }
    this.setState({ sourceProviderSetId: defaultSourceProviderSetId });

    const sourceProviderSetOptions =
      activeProviderSets.map((providerSet) => {
        return <option key={providerSet.providerSetId} value={providerSet.providerSetId}>{`${providerSet.name} - ${moment.utc(providerSet.lastActivationDate).local().format("MMM DD, YYYY h:mm:ss A")}`}</option>
      });

    this.setState({
      enableSourceEditing: true,
      sourceProviderSetOptions: sourceProviderSetOptions
    });
  }

  private formatSourceProviderSetName(name: string, activationDate: Date): string {
    return `${name} - ${moment.utc(activationDate).local().format("MMM DD, YYYY h:mm:ss A")}`;
  }

  private async updateFilterColumnNames() {
    if (!this.state.sourceProviderSetId || this.props.providerSet.sourceProviderSetId === this.state.sourceProviderSetId || !this.state.enableSourceEditing)
      return;

    this.setState({ loading: true });

    this.setState({
      filterCriteriaColumnNames: await this.providerSetService.getFilterProviderSetColumnNames(this.state.sourceProviderSetId.toString()),
      loading: false
    });
  }

  public render() {
    return (
      <>
        {this.props.loading ? <LoadingSpinner /> :
          <>
            <div className="col-9">
              {!!this.props.providerSet.sourceProviderSetId ?
                !!this.state.enableSourceEditing ?
                  <QESSelect
                    testId="edit-provider-set-panel-content__source"
                    containerClassName="mb-3"
                    label="Source Network"
                    value={this.state.sourceProviderSetId}
                    onChange={(e) => {
                      this.setState(
                        { sourceProviderSetId: parseInt(e.target.value) },
                        () => { this.updateFilterColumnNames(); }
                      );
                    }}
                  >
                    {this.state.sourceProviderSetOptions}
                    required={true}
                  </QESSelect>
                  : <>
                    <label className="label-s mb-0" htmlFor="edit-provider-set-panel-content__source-name">Source Network: </label>
                    <p className="mb-2" id="edit-provider-set-panel-content__source-name">{this.formatSourceProviderSetName(this.props.sourceProviderSet.resource.name, this.props.sourceProviderSetVersion.dateActivated)}</p>
                  </>
                : null
              }
              <QESTextInput
                testId={"edit-provider-set-panel-content__name"}
                containerClassName="mb-3"
                label="Name"
                value={this.state.name}
                onChange={(e) => this.setState({ name: e.target.value })}
              />
              <QESTextArea
                testId={"edit-provider-set-panel-content__description"}
                containerClassName="mb-3"
                label="Description"
                value={this.state.description}
                onChange={(e) => this.setState({ description: e.target.value })}
              />
              {this.props.showIsPrivate ?
                <QESCheckbox
                  testId={"edit-provider-set-panel-content__private"}
                  containerClassName="mb-3"
                  label="This is a Private Resource (can be administered only by you)"
                  checked={this.state.isPrivate}
                  onChange={(e) => this.setState({ isPrivate: e.target.checked })} />
                : null
              }
              {!!this.state.sourceProviderSetId && this.props.providerSet.sourceProviderSetId !== this.state.sourceProviderSetId ?
                <FilterCriteria
                  fieldNames={this.state.filterCriteriaColumnNames}
                  defaultCriteria={this.props.filterCriteria}
                  disabled={!this.props.enableSourceEditing}
                  onChange={(response) => {
                    this.setState({
                      filterCriteria: response.criteria,
                      filterCriteriaHasErrors: response.hasErrors
                    })
                  }} />
                : null
              }
            </div>
            <FlyoutFooter
              submitButton={
                <QESButton qesTestId="edit-provider-set-panel-content__confirm"
                  disabled={
                    (this.props.providerSet.resource.name === this.state.name && this.props.providerSet.resource.description === this.state.description && this.props.providerSet.resource.isPrivate === this.state.isPrivate && this.props.providerSet.sourceProviderSetId === this.state.sourceProviderSetId) ||
                    (this.state.filterCriteriaHasErrors)

                  }
                  onClick={() => {
                    this.props.handleCloseFlyout().then(() => {
                      this.props.handleSave(this.state.name, this.state.description, this.state.isPrivate, this.state.sourceProviderSetId, this.state.filterCriteria);
                    });
                  }}
                >Save</QESButton>
              }
            />
          </>
        }
      </>
    );
  }
}

const EditPanelContent = withFeatureFlagContext(withFlyoutContext(WrappedEditPanelContent));
export default EditPanelContent;
